import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
import {
  EyeIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import config from '../config/api';

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const token = localStorage.getItem('token');
      const statusParam = filterStatus !== 'ALL' ? `?status_filter=${filterStatus}` : '';
      const response = await fetch(`${config.CONTRACTS_URL}${statusParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [filterStatus]);

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-slate-100 text-slate-700',
      'ACTIVE': 'status-active',
      'COMPLETED': 'status-completed',
      'CANCELLED': 'status-expired',
      'SUSPENDED': 'status-pending'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'DRAFT': 'Bản nháp',
      'ACTIVE': 'Đang hiệu lực',
      'COMPLETED': 'Hoàn thành',
      'CANCELLED': 'Đã hủy',
      'SUSPENDED': 'Tạm dừng'
    };
    return statusMap[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <ClockIcon className="w-4 h-4" />;
      case 'COMPLETED':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'CANCELLED':
        return <XCircleIcon className="w-4 h-4" />;
      case 'SUSPENDED':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <DocumentTextIcon className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'undefined' || amount === null || amount === 0) {
      return '0 ₫';
    }
    return new Intl.NumberFormat('vi-VN').format(amount) + ' ₫';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Chưa xác định';
    return dayjs.utc(dateString).tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY');
  };

  const getPaymentProgress = (contract) => {
    if (!contract.payment_schedules || contract.payment_schedules.length === 0) {
      return { percentage: 0, paid: 0, total: contract.total_amount || 0 };
    }

    const paidAmount = contract.payment_schedules
      .filter(schedule => schedule.status === 'PAID')
      .reduce((sum, schedule) => sum + (schedule.amount || 0), 0);

    const totalAmount = contract.total_amount || 0;
    const percentage = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;

    return { percentage, paid: paidAmount, total: totalAmount };
  };

  const getPaymentColor = (percentage) => {
    if (percentage >= 100) return 'from-emerald-400 to-emerald-600';
    if (percentage >= 50) return 'from-blue-400 to-blue-600';
    if (percentage >= 25) return 'from-amber-400 to-amber-600';
    return 'from-slate-300 to-slate-400';
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesStatus = filterStatus === 'ALL' || contract.status === filterStatus;
    const contractNumber = contract.contract_number || `Hợp đồng #${contract.id}`;
    const projectName = contract.project?.name || '';
    const matchesSearch = contractNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         projectName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Calculate stats
  const stats = {
    total: contracts.length,
    active: contracts.filter(c => c.status === 'ACTIVE').length,
    completed: contracts.filter(c => c.status === 'COMPLETED').length,
    totalValue: contracts.reduce((sum, c) => sum + (c.total_amount || 0), 0)
  };

  const handleViewDetails = (contract) => {
    setSelectedContract(contract);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-primary-200 rounded-full"></div>
          <div className="w-20 h-20 border-4 border-primary-500 rounded-full animate-spin absolute top-0 left-0 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <DocumentTextIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-sm text-slate-500">Tổng hợp đồng</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <ArrowTrendingUpIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.active}</p>
              <p className="text-sm text-slate-500">Đang hiệu lực</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-xl">
              <CheckCircleIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.completed}</p>
              <p className="text-sm text-slate-500">Hoàn thành</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <BanknotesIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-800">{new Intl.NumberFormat('vi-VN').format(stats.totalValue / 1000000)}M</p>
              <p className="text-sm text-slate-500">Tổng giá trị</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Search */}
      <div className="glass-card rounded-2xl p-5">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm kiếm hợp đồng..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input-modern pl-12"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-input-modern w-auto"
            >
              <option value="ALL">Tất cả trạng thái</option>
              <option value="DRAFT">Bản nháp</option>
              <option value="ACTIVE">Đang hiệu lực</option>
              <option value="COMPLETED">Hoàn thành</option>
              <option value="SUSPENDED">Tạm dừng</option>
              <option value="CANCELLED">Đã hủy</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contracts Grid */}
      {filteredContracts.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <DocumentTextIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Không tìm thấy hợp đồng</h3>
          <p className="text-slate-500">
            {filterStatus === 'ALL'
              ? "Bạn chưa có hợp đồng nào."
              : `Không có hợp đồng nào với trạng thái "${getStatusText(filterStatus)}".`
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContracts.map((contract, index) => {
            const paymentProgress = getPaymentProgress(contract);

            return (
              <div
                key={contract.id}
                className="card-interactive rounded-2xl overflow-hidden"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Card Header with gradient */}
                <div className={`h-2 bg-gradient-to-r ${getPaymentColor(paymentProgress.percentage)}`}></div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 mr-3">
                      <h3 className="text-lg font-bold text-slate-800 line-clamp-1">
                        {contract.contract_number || `HĐ-${contract.id}`}
                      </h3>
                      {contract.project && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                          {contract.project.name}
                        </p>
                      )}
                    </div>
                    <span className={`status-badge ${getStatusColor(contract.status)} flex-shrink-0`}>
                      {getStatusIcon(contract.status)}
                      <span className="ml-1">{getStatusText(contract.status)}</span>
                    </span>
                  </div>

                  {/* Contract Value */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-4">
                    <p className="text-sm text-emerald-600 font-medium mb-1">Giá trị hợp đồng</p>
                    <p className="text-2xl font-bold text-emerald-700">
                      {formatCurrency(contract.total_amount)}
                    </p>
                  </div>

                  {/* Payment Progress */}
                  <div className="mb-5">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500 font-medium">Tiến độ thanh toán</span>
                      <span className="font-bold text-slate-700">{paymentProgress.percentage}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className={`progress-bar-fill bg-gradient-to-r ${getPaymentColor(paymentProgress.percentage)}`}
                        style={{ width: `${paymentProgress.percentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      <span>Đã thanh toán: {formatCurrency(paymentProgress.paid)}</span>
                    </div>
                  </div>

                  {/* Date Info */}
                  <div className="space-y-2 text-sm text-slate-500 mb-5">
                    {contract.signed_date && (
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                        <span>Ngày ký: {formatDate(contract.signed_date)}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <button
                    onClick={() => handleViewDetails(contract)}
                    className="w-full btn-gradient-primary flex items-center justify-center gap-2"
                  >
                    <EyeIcon className="w-5 h-5" />
                    Xem chi tiết
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Contract Details Modal */}
      {showModal && selectedContract && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in">
            {/* Modal Header */}
            <div className={`h-3 bg-gradient-to-r ${getPaymentColor(getPaymentProgress(selectedContract).percentage)}`}></div>

            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    {selectedContract.contract_number || `Hợp đồng #${selectedContract.id}`}
                  </h2>
                  <span className={`status-badge ${getStatusColor(selectedContract.status)}`}>
                    {getStatusIcon(selectedContract.status)}
                    <span className="ml-1">{getStatusText(selectedContract.status)}</span>
                  </span>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <XCircleIcon className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Project Info */}
                {selectedContract.project && (
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-2">Dự án</h4>
                    <div className="bg-blue-50 p-4 rounded-xl">
                      <p className="font-medium text-blue-800">{selectedContract.project.name}</p>
                      {selectedContract.project.description && (
                        <p className="text-blue-600 text-sm mt-1">{selectedContract.project.description}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Contract Value & Dates */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-5 rounded-xl text-white">
                    <div className="text-sm opacity-80 mb-1">Giá trị hợp đồng</div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(selectedContract.total_amount)}
                    </div>
                  </div>
                  {selectedContract.signed_date && (
                    <div className="bg-slate-50 p-5 rounded-xl">
                      <div className="text-sm text-slate-600 mb-1">Ngày ký</div>
                      <div className="font-bold text-slate-800">{formatDate(selectedContract.signed_date)}</div>
                    </div>
                  )}
                  <div className="bg-slate-50 p-5 rounded-xl">
                    <div className="text-sm text-slate-600 mb-1">Ngày tạo</div>
                    <div className="font-bold text-slate-800">{formatDate(selectedContract.created_at)}</div>
                  </div>
                </div>

                {/* Payment Progress */}
                {selectedContract.payment_schedules && selectedContract.payment_schedules.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-3">Lịch thanh toán</h4>
                    <div className="bg-slate-50 p-5 rounded-xl mb-4">
                      <div className="flex justify-between text-sm text-slate-600 mb-2">
                        <span>Tiến độ tổng thể</span>
                        <span className="font-bold">{getPaymentProgress(selectedContract).percentage}%</span>
                      </div>
                      <div className="h-4 bg-slate-200 rounded-full overflow-hidden mb-2">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${getPaymentColor(getPaymentProgress(selectedContract).percentage)} transition-all duration-500`}
                          style={{ width: `${getPaymentProgress(selectedContract).percentage}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-sm text-slate-500">
                        <span>Đã thanh toán: {formatCurrency(getPaymentProgress(selectedContract).paid)}</span>
                        <span>Còn lại: {formatCurrency(getPaymentProgress(selectedContract).total - getPaymentProgress(selectedContract).paid)}</span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {selectedContract.payment_schedules.map((schedule, index) => (
                        <div key={schedule.id || index} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
                          <div>
                            <div className="font-semibold text-slate-800">{schedule.phase_name || `Đợt ${index + 1}`}</div>
                            <div className="text-sm text-slate-500">
                              Hạn: {formatDate(schedule.due_date)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-slate-800 mb-1">{formatCurrency(schedule.amount)}</div>
                            <span className={`status-badge ${
                              schedule.status === 'PAID' ? 'status-active' :
                              schedule.status === 'OVERDUE' ? 'status-expired' :
                              'status-pending'
                            }`}>
                              {schedule.status === 'PAID' ? 'Đã thanh toán' :
                               schedule.status === 'PENDING' ? 'Chờ thanh toán' :
                               schedule.status === 'OVERDUE' ? 'Quá hạn' :
                               schedule.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Terms */}
                {selectedContract.terms && (
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-2">Điều khoản</h4>
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedContract.terms}</p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedContract.notes && (
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-2">Ghi chú</h4>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedContract.notes}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-semibold"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
